import { useEffect, useState } from "react";
import { Copy, Check, Send, Share2, MessageCircle, Mail } from "lucide-react";

/**
 * Prominent invite-link share panel for a newly-generated portal invite.
 * Shows the URL big and copyable + one-tap WhatsApp / SMS / Email / native Share.
 */
export default function InvitePortalShare({ client, link, emailSent }) {
    const [copied, setCopied] = useState(false);
    useEffect(() => {
        if (!copied) return;
        const t = setTimeout(() => setCopied(false), 1800);
        return () => clearTimeout(t);
    }, [copied]);

    const firstName = client?.name?.split(" ")[0] || "there";
    const message = `Hey ${firstName} — your Next Level Body Lab athlete portal is ready. Sign in here to view your program, message me, and log progress:\n\n${link}`;

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(link);
            setCopied(true);
        } catch (_e) {
            // Fallback for older mobile browsers
            const ta = document.createElement("textarea");
            ta.value = link; document.body.appendChild(ta); ta.select();
            try { document.execCommand("copy"); setCopied(true); } catch (_) {}
            document.body.removeChild(ta);
        }
    };

    const sharedNative = async () => {
        if (navigator.share) {
            try { await navigator.share({ title: "Next Level Body Lab Portal", text: message, url: link }); } catch (_e) {}
        }
    };

    const sms = `sms:${client?.phone || ""}?&body=${encodeURIComponent(message)}`;
    const whatsapp = `https://wa.me/${(client?.phone || "").replace(/\D/g, "")}?text=${encodeURIComponent(message)}`;
    const mailto = `mailto:${client?.email || ""}?subject=${encodeURIComponent("Your Next Level Body Lab portal is ready")}&body=${encodeURIComponent(message)}`;

    return (
        <div className="mt-4 border border-[var(--gold)] bg-[var(--surface-2)] p-4 space-y-3" data-testid="invite-share-panel">
            <div className="flex items-center gap-2">
                <Send size={14} className="text-[var(--gold-bright)]" />
                <p className="text-sm font-serif">
                    {emailSent ? "Email sent." : `${firstName}'s invite link is ready.`}
                </p>
            </div>
            {!emailSent && (
                <p className="text-[11px] text-[var(--text-muted)]">
                    Send this link to {firstName} however you like. It's a one-time sign-up — once they tap it, they'll set a password and land in their portal.
                </p>
            )}
            <div
                onClick={copy}
                className="font-mono text-[10px] break-all bg-[var(--bg-deep)] border border-[var(--border-bright)] hover:border-[var(--gold)] p-3 cursor-pointer transition-colors"
                title="Tap to copy"
                data-testid="invite-link-preview">
                {link}
            </div>
            <div className="grid grid-cols-2 gap-2">
                <button onClick={copy} className="btn-primary inline-flex items-center justify-center gap-2 text-xs" data-testid="invite-copy-btn">
                    {copied ? <><Check size={12} /> Copied!</> : <><Copy size={12} /> Copy link</>}
                </button>
                {typeof navigator !== "undefined" && navigator.share && (
                    <button onClick={sharedNative} className="btn-ghost inline-flex items-center justify-center gap-2 text-xs" data-testid="invite-share-btn">
                        <Share2 size={12} /> Share…
                    </button>
                )}
                {client?.phone && (
                    <>
                        <a href={whatsapp} target="_blank" rel="noreferrer"
                           className="btn-ghost inline-flex items-center justify-center gap-2 text-xs" data-testid="invite-whatsapp-btn">
                            <MessageCircle size={12} /> WhatsApp
                        </a>
                        <a href={sms}
                           className="btn-ghost inline-flex items-center justify-center gap-2 text-xs" data-testid="invite-sms-btn">
                            <MessageCircle size={12} /> SMS
                        </a>
                    </>
                )}
                {client?.email && (
                    <a href={mailto}
                       className="btn-ghost inline-flex items-center justify-center gap-2 text-xs col-span-2" data-testid="invite-email-btn">
                        <Mail size={12} /> Email it to {client.email}
                    </a>
                )}
            </div>
        </div>
    );
}
